/**
 * Step 3 - Defaults & Opções (importação de CLIENTES).
 *
 * Customer import é mais simples que deal import:
 *   - Sem workspace/pipeline/fase/vendedor (cliente não tem fluxo de vendas)
 *   - Dedupe é sempre 'skip' (decisão do negócio: nunca atualizar registros existentes via CSV)
 *
 * Toggles disponíveis:
 *   - Modo silencioso (suprime triggers/automações pesadas)
 *   - Código do país padrão para normalização de telefone (Select com bandeiras,
 *     mesmo padrão visual do wizard de Importar Contatos — CSVUploadStep)
 *
 * RELEASE: 2026-05-18 v1.1.0 - Substituído Input livre de DDI por Select com
 *          bandeiras (reaproveita padrão do wizard de Importar Contatos).
 * RELEASE: 2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Info, Globe } from 'lucide-react';
import { countryCodes } from '@/lib/country-codes';

interface Step3DefaultsCustomersProps {
  silentMode: boolean;
  defaultCountryCode: string;
  onSilentModeChange: (v: boolean) => void;
  onDefaultCountryCodeChange: (v: string) => void;
}

export function Step3DefaultsCustomers(props: Step3DefaultsCustomersProps) {
  const { silentMode, defaultCountryCode, onSilentModeChange, onDefaultCountryCodeChange } = props;

  // País selecionado para exibição no trigger do Select (bandeira + nome + código)
  const selectedCountry = countryCodes.find(
    (c) => c.code.replace('+', '') === defaultCountryCode,
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-3 rounded-md border border-blue-300/60 bg-blue-50 p-3 text-blue-900 dark:border-blue-400/30 dark:bg-blue-950/40 dark:text-blue-100">
        <Info className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Comportamento de duplicidade</p>
          <p className="text-xs opacity-90 mt-0.5">
            Clientes com CPF/CNPJ já existentes serão <strong>pulados</strong> automaticamente
            (estratégia "skip"). Não é possível atualizar registros existentes via CSV — para
            isso, use a edição manual do cliente.
          </p>
        </div>
      </div>

      {/* Seletor de DDI padrão — mesmo padrão visual do wizard de Importar Contatos */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <Label className="font-medium text-sm">Código do País (DDI)</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Será adicionado automaticamente nos telefones que não tiverem DDI
            </p>
          </div>
          <Select value={defaultCountryCode} onValueChange={onDefaultCountryCodeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue>
                {selectedCountry
                  ? `${selectedCountry.flag} ${selectedCountry.country} (${selectedCountry.code})`
                  : 'Selecione...'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {countryCodes.slice(0, 10).map((country) => (
                <SelectItem key={country.code} value={country.code.replace('+', '')}>
                  {country.flag} {country.country} ({country.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label className="text-base">Modo silencioso</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Suprime notificações e automações durante a importação. Recomendado para volumes grandes.
          </p>
        </div>
        <Switch checked={silentMode} onCheckedChange={onSilentModeChange} />
      </div>
    </div>
  );
}
